import React, { useRef, useState } from 'react';
import { X } from 'lucide-react'; // or wherever your X icon comes from
import MapComponent from './Manager/Components/MapComponent';
import type { Restaurant } from './Manager/restaurant.types';
import { generateUrl, useUserLocation, type UserLocation } from './Public/Explore/services';
import { IoMdLocate } from "react-icons/io";
import { searchNearbyRestaurants } from '../lib/api/restaurant.apis';

interface MapOverlayProps {
    show: boolean;
    onClose: () => void;
    restaurants: Restaurant[];
}
const MapOverlay: React.FC<MapOverlayProps> = ({ show, onClose, restaurants = [] }) => {
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { location } = useUserLocation();

  const errorTimeout = useRef<NodeJS.Timeout | null>(null);

  const showError = (message: string) => {
    setError(message);

    if (errorTimeout.current) clearTimeout(errorTimeout.current);

    errorTimeout.current = setTimeout(() => {
      setError("");
    }, 3000);
  };

  const handleNearby = async () => {
    try {
      setLoading(true);

      if(!location) {
        setError("Please allow location access then continue");
        return;
      }
      const lat = location.lat;
      const lng = location.lng;

      const res = await searchNearbyRestaurants(lat, lng);

      if (res.count === 0 || res.message === "Latitude and longitude are required") {
        setNearbyRestaurants([]);
        showError("Allow Location access then proceed");
        return;
      }
      
      setNearbyRestaurants(res.restaurants);
    } catch (error) {
      if (error.code === "ECONNREFUSED") {
        showError("Network error, check connection");
      } else if (error.response) {
        // Server returned an error status
        showError(error.response.data.message || "Please allow location first");
      } else {
        // Other errors
        showError(error.message || "Please allow location first");
      } 
    } finally{
      setLoading(false);
    }
  }

  return (
    <div
      className={`fixed inset-x-0 bottom-0 bg-white border-t-2 border-gray-300 shadow-2xl transition-transform duration-300 ease-in-out z-300 ${
        show ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{ height: '70vh' }}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-2 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Map View</h3>
          <div className='flex gap-4'>
            <button
              onClick={handleNearby}
              disabled={loading}
              className={`flex items-center px-4 py-2 text-white rounded-full hover:bg-gray-800 transition font-medium cursor-pointer
              ${loading 
                ? "bg-gray-400 text-white cursor-not-allowed opacity-60" 
                : "bg-gray-900 text-white hover:bg-gray-800 cursor-pointer"
              } 
             `}
            >
              <IoMdLocate className="inline mr-1" size={17} />{loading ? "Searching... " : "Nearby"}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div
          className={`
            px-8 py-2 border-b text-sm transition-all duration-500 transform
            ${error ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none h-0 p-0 border-0"}
            bg-red-50 border-red-200 text-red-700
            overflow-hidden
          `}
        >
          {error}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-hidden">
            <MapComponent
            searchable={false}
            interactive={false}
            locations={[...restaurants, ...(nearbyRestaurants || [])]
                .filter(r => r.location?.geo?.coordinates?.length === 2)
                .map(r => ({
                lat: r.location.geo.coordinates[1], // latitude
                lng: r.location.geo.coordinates[0],  // longitude
                restaurant: {
                  id: r._id, 
                  name: r.name,
                  rating: r.rating.average,
                  cover: generateUrl(r.gallery?.cover?.url)
                }
                }))}
            />
        </div>
      </div>
    </div>
  );
};

export default MapOverlay;
